<template>
  <header class="adm-header" :style="{ '--sb-w': sidebarWidth }">
    <div class="adm-header__inner">
      <!-- Izquierda: hamburguesa (móvil) + título de página -->
      <button
        class="adm-header__hamburger"
        @click="toggleSidebar"
        :aria-expanded="collapsed ? 'true' : 'false'"
        aria-controls="adm-sidebar"
        aria-label="Abrir menú de navegación">
        <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round">
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </svg>
      </button>

      <div class="adm-header__title" aria-current="page">{{ pageTitle }}</div>

      <!-- Derecha: cuenta -->
      <div class="adm-header__actions">
        <div class="acct" ref="acct">
          <button
            class="acct__btn"
            :class="{ 'acct__btn--open': menuOpen }"
            type="button"
            @click="toggleMenu"
            :aria-expanded="menuOpen ? 'true' : 'false'"
            aria-haspopup="menu"
            aria-label="Menú de cuenta">
            <span class="acct__avatar" :class="{ 'acct__avatar--img': acctLogoUrl }" aria-hidden="true">
              <img v-if="acctLogoUrl" :src="acctLogoUrl" alt="" @error="companyImgError = true" />
              <template v-else>{{ acctInitials }}</template>
            </span>
            <span class="acct__meta">
              <span class="acct__name">{{ acctName }}</span>
              <span class="acct__role">{{ roleName }}</span>
            </span>
            <svg class="acct__chev" :class="{ 'acct__chev--open': menuOpen }" aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>

          <transition name="acct-menu">
            <div v-if="menuOpen" class="acct__menu" role="menu" @keydown.esc="closeMenu">
              <div class="acct__head">
                <span class="acct__avatar acct__avatar--lg" :class="{ 'acct__avatar--img': acctLogoUrl }" aria-hidden="true">
                  <img v-if="acctLogoUrl" :src="acctLogoUrl" alt="" @error="companyImgError = true" />
                  <template v-else>{{ acctInitials }}</template>
                </span>
                <div class="acct__head-meta">
                  <p class="acct__menu-name">{{ acctName }}</p>
                  <p class="acct__menu-email">{{ acctEmail }}</p>
                </div>
              </div>
              <div class="acct__sep" role="separator"></div>
              <button class="acct__item acct__item--danger" type="button" role="menuitem" @click="handleLogout">
                <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
                Cerrar sesión
              </button>
            </div>
          </transition>
        </div>
      </div>
    </div>
  </header>
</template>

<script>
import { collapsed, toggleSidebar, sidebarWidth } from "../sidebar/state";
import { UsersStorage } from "@/db/storage";
import authService from "@/services/auth.service";
import UsuarioService from "@/services/usuario.service";

// Títulos por ruta (prefijo). El más largo que coincida gana,
// así las subpáginas de detalle conservan el título de su sección.
const TITLES = {
  "/admin/import/messages": "Importar mensajes",
  "/admin/messages": "Mensajes recibidos",
  "/admin/administracion/area": "Áreas",
  "/admin/administracion/report": "Tipos de reporte",
  "/admin/administracion": "Usuarios",
  "/admin/configuracion/empresas": "Empresas",
  "/admin/configuracion/nuevaempresas": "Nueva empresa",
  "/admin/configuracion/administradores": "Multiusuarios",
  "/admin/configuracion": "Opciones",
  "/admin/create": "Nuevo reporte",
  "/estadisticos": "Estadísticos",
  "/admin": "Inicio",
};

const ROLE_NAMES = {
  superadmin: "Superadministrador",
  admin: "Administrador",
  manager: "Gestionador",
  reader: "Lector",
};

const COMPANY_ROLES = ["admin", "manager", "reader"];

export default {
  name: "HeaderAdminPartial",

  data() {
    return {
      usersStorage: new UsersStorage(),
      users: null,
      company: null,
      companyImgError: false,
      menuOpen: false,
    };
  },

  setup() {
    return { collapsed, toggleSidebar, sidebarWidth };
  },

  computed: {
    pageTitle() {
      const path = this.$route.path;
      const match = Object.keys(TITLES)
        .sort((a, b) => b.length - a.length)
        .find((p) => path === p || path.startsWith(p + "/"));
      return match ? TITLES[match] : "Panel";
    },
    userEmail() {
      return this.users?.email || "";
    },
    displayName() {
      return this.users?.fullname || this.users?.email || "Usuario";
    },
    roleName() {
      return ROLE_NAMES[this.users?.rol] || this.users?.rol || "";
    },
    initials() {
      const src = this.users?.fullname || this.users?.email || "?";
      const parts = src
        .trim()
        .split(/[\s@.]+/)
        .filter(Boolean);
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
      return src[0].toUpperCase();
    },
    isCompanyUser() {
      return COMPANY_ROLES.includes(this.users?.rol);
    },
    isSuperadmin() {
      return this.users?.rol === "superadmin";
    },
    companyName() {
      return this.company?.name_trade || this.company?.name || "";
    },
    companyLogoUrl() {
      if (this.companyImgError) return null;
      return this.company?.photo?.link || this.company?.logo || null;
    },
    companyInitials() {
      const src = this.companyName || "?";
      const parts = src.trim().split(/\s+/).filter(Boolean);
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
      return src.substring(0, 2).toUpperCase();
    },
    // Identidad mostrada en el navbar (empresa para roles de empresa; HR Outservice para superadmin)
    acctName() {
      if (this.isCompanyUser) return this.companyName || this.displayName;
      if (this.isSuperadmin) return "HR Outservice";
      return this.displayName;
    },
    acctEmail() {
      return this.userEmail;
    },
    acctLogoUrl() {
      return this.isCompanyUser ? this.companyLogoUrl : null;
    },
    acctInitials() {
      return this.isCompanyUser ? this.companyInitials || this.initials : this.initials;
    },
  },

  mounted() {
    this.loadUserData();
    window.addEventListener("storage", this.onStorage);
    document.addEventListener("click", this.onDocClick);
  },

  beforeUnmount() {
    window.removeEventListener("storage", this.onStorage);
    document.removeEventListener("click", this.onDocClick);
  },

  methods: {
    async loadUserData() {
      try {
        const user = await authService.getCurrentUser();
        this.users = user || this.usersStorage.get();
      } catch {
        this.users = this.usersStorage.get();
      }
      const raw = localStorage.getItem("company");
      this.company = raw ? JSON.parse(raw) : this.users?.company || null;
      this.companyImgError = false;
    },
    onStorage(e) {
      if (["user", "company", null].includes(e.key)) this.loadUserData();
    },
    toggleMenu() {
      this.menuOpen = !this.menuOpen;
    },
    closeMenu() {
      this.menuOpen = false;
    },
    onDocClick(e) {
      if (this.menuOpen && this.$refs.acct && !this.$refs.acct.contains(e.target)) {
        this.menuOpen = false;
      }
    },
    async handleLogout() {
      this.menuOpen = false;
      try {
        authService.clearCache();
        try {
          await UsuarioService.logout();
        } catch {
          /* silent */
        }
        this.usersStorage.remove();
        ["accessToken", "user", "company", "slug"].forEach((k) => localStorage.removeItem(k));
        if (collapsed.value) toggleSidebar();
        this.$router.push({ path: "/", replace: true });
      } catch {
        authService.clearCache();
        localStorage.clear();
        window.location.href = "/";
      }
    },
  },
};
</script>

<style scoped>
.adm-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 60px;
  background: #355e91;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  z-index: 999;
  transition: left 0.16s cubic-bezier(0.4, 0, 0.2, 1);
}

@media (min-width: 1024px) {
  .adm-header {
    left: var(--sb-w, 250px);
  }
}

.adm-header__inner {
  height: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 16px;
}

/* Hamburguesa (solo móvil) */
.adm-header__hamburger {
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}

.adm-header__hamburger:hover {
  background: rgba(255, 255, 255, 0.2);
}

.adm-header__hamburger:focus-visible {
  outline: 2px solid #fff;
  outline-offset: 2px;
}

@media (min-width: 1024px) {
  .adm-header__hamburger {
    display: none;
  }
}

/* Título de página */
.adm-header__title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #ffffff;
  letter-spacing: -0.01em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Acciones (derecha) */
.adm-header__actions {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 8px;
}

.acct {
  position: relative;
}

.acct__btn {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 4px 10px 4px 4px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 999px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.13), rgba(255, 255, 255, 0.06));
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.12);
  color: #fff;
  cursor: pointer;
  transition: background 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.acct__btn:hover {
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.11));
  border-color: rgba(255, 255, 255, 0.34);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.18), 0 4px 12px rgba(0, 0, 0, 0.14);
  transform: translateY(-1px);
}

.acct__btn:active {
  transform: translateY(0);
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.12);
}

/* Estado abierto: se mantiene "presionado" mientras el menú está visible */
.acct__btn--open {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.4);
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.1);
}

.acct__btn--open:hover {
  transform: none;
}

.acct__btn:focus-visible {
  outline: 2px solid #fff;
  outline-offset: 2px;
}

.acct__avatar {
  width: 32px;
  height: 32px;
  min-width: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.22);
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: #fff;
  letter-spacing: 0.02em;
  overflow: hidden;
}

.acct__avatar--img {
  background: #fff;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.4), 0 1px 3px rgba(0, 0, 0, 0.12);
}

.acct__avatar--img img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 50%;
}

.acct__meta {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0;
  line-height: 1.25;
  max-width: 160px;
}

.acct__name {
  font-size: 13px;
  font-weight: 650;
  letter-spacing: -0.01em;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 160px;
}

.acct__role {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: rgba(255, 255, 255, 0.62);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 160px;
}

.acct__chev {
  margin-left: 2px;
  color: rgba(255, 255, 255, 0.65);
  transition: transform 0.2s cubic-bezier(0.34, 1.4, 0.64, 1), color 0.18s ease;
}

.acct__btn:hover .acct__chev {
  color: rgba(255, 255, 255, 0.95);
}

.acct__chev--open {
  transform: rotate(180deg);
  color: #fff;
}

/* Ocultar nombre/rol en pantallas chicas (deja solo el avatar) */
@media (max-width: 600px) {
  .acct__meta,
  .acct__chev {
    display: none;
  }
  .acct__btn {
    padding: 4px;
    border-radius: 50%;
  }
}

/* Menú desplegable */
.acct__menu {
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  min-width: 240px;
  background: #fff;
  border: 1px solid #e4e7ec;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05), 0 12px 32px rgba(0, 0, 0, 0.12);
  padding: 6px;
  z-index: 10;
}

.acct__head {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 10px 10px 12px;
}

.acct__head .acct__avatar--lg {
  width: 40px;
  height: 40px;
  min-width: 40px;
  background: #355e91;
  font-size: 14px;
}

.acct__head .acct__avatar--lg.acct__avatar--img {
  background: #fff;
  border: 1px solid #e5e7eb;
}

.acct__head-meta {
  min-width: 0;
}

.acct__menu-name {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 170px;
}

.acct__menu-email {
  margin: 2px 0 0;
  font-size: 12px;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 170px;
}

.acct__sep {
  height: 1px;
  background: #eef0f3;
  margin: 2px 4px 4px;
}

.acct__item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 9px 10px;
  border: none;
  border-radius: 8px;
  background: transparent;
  font: inherit;
  font-size: 13.5px;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  transition: background 0.13s;
  white-space: nowrap;
}

.acct__item:hover {
  background: #f3f4f6;
}

.acct__item:focus-visible {
  outline: 2px solid #355e91;
  outline-offset: -2px;
}

.acct__item--danger {
  color: #dc2626;
}

.acct__item--danger:hover {
  background: #fef2f2;
}

/* Transición del menú */
.acct__menu {
  transform-origin: top right;
  will-change: transform, opacity;
}

.acct-menu-enter-active {
  transition: opacity 0.18s ease, transform 0.2s cubic-bezier(0.34, 1.4, 0.64, 1);
}
.acct-menu-leave-active {
  transition: opacity 0.13s ease, transform 0.13s ease;
}
.acct-menu-enter-from,
.acct-menu-leave-to {
  opacity: 0;
  transform: translateY(-8px) scale(0.96);
}

@media (prefers-reduced-motion: reduce) {
  .acct__chev,
  .acct-menu-enter-active,
  .acct-menu-leave-active {
    transition: none;
  }
}
</style>
